import { useCallback, useEffect, useMemo, useState } from "react";
import {
  WHATS_NEW_RELEASE,
  type WhatsNewItem,
  type WhatsNewItemId,
  type WhatsNewRelease,
} from "../constants/whatsNew";

/**
 * Per-user, per-device "what's new" state, kept in localStorage: which release items are done
 * and whether the latest release's Getting Started step is complete.
 *
 * Deliberately client-only: losing it (private window, cleared storage, a second device) just
 * means the latest release shows as not reviewed again. Nothing here may block rendering, and
 * every storage access is wrapped because Safari private mode / blocked site data can throw on
 * read *and* write. An in-memory copy keeps the session consistent when storage is unavailable.
 */

type ItemRecord = { doneAt?: string };
type ReleaseRecord = { completedAt?: string; homeCardDismissedAt?: string };

export type WhatsNewStoredState = {
  /**
   * Per release: when the "Review the latest release" step was completed (every card done, or
   * the step's own "Mark as done"), and when they dismissed the home-screen card for that release.
   */
  releases: Record<string, ReleaseRecord>;
  /** Per release item: when it was marked done (Mark as done, its CTA, or visiting it). */
  items: Partial<Record<string, ItemRecord>>;
};

export const WHATS_NEW_STORAGE_PREFIX = "nfp.whatsNew.v1";
export const WHATS_NEW_CHANGED_EVENT = "nfp:whats-new-changed";

export const getWhatsNewStorageKey = (userId: string) =>
  `${WHATS_NEW_STORAGE_PREFIX}:${userId}`;

const emptyState = (): WhatsNewStoredState => ({ releases: {}, items: {} });

// Session fallback + cross-instance consistency when localStorage throws.
const memoryState = new Map<string, WhatsNewStoredState>();

/** Test helper: forget the in-memory fallback between tests. */
export const resetWhatsNewMemoryState = () => memoryState.clear();

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const getStorage = (): StorageLike | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

const pickString = (value: unknown) =>
  typeof value === "string" ? value : undefined;

/**
 * Parse stored state, migrating older shapes: a legacy `visitedAt` counts as done, and
 * `dismissedAt` / `hiddenAt` / `firstSeenAt` are dropped (hiding and expiry no longer exist,
 * so previously hidden items come back). A release's legacy `reviewedAt` is dropped too: it was
 * stamped just by opening the dialog, which no longer completes the step.
 */
const normalize = (value: unknown): WhatsNewStoredState => {
  if (!isRecord(value)) return emptyState();
  const releases: Record<string, ReleaseRecord> = {};
  if (isRecord(value.releases)) {
    for (const [id, raw] of Object.entries(value.releases)) {
      if (!isRecord(raw)) continue;
      const record: ReleaseRecord = {};
      const completedAt = pickString(raw.completedAt);
      const homeCardDismissedAt = pickString(raw.homeCardDismissedAt);
      if (completedAt) record.completedAt = completedAt;
      if (homeCardDismissedAt) record.homeCardDismissedAt = homeCardDismissedAt;
      releases[id] = record;
    }
  }
  const items: Partial<Record<string, ItemRecord>> = {};
  if (isRecord(value.items)) {
    for (const [id, raw] of Object.entries(value.items)) {
      if (!isRecord(raw)) continue;
      const doneAt = pickString(raw.doneAt) ?? pickString(raw.visitedAt);
      if (doneAt) items[id] = { doneAt };
    }
  }
  return { releases, items };
};

export const readWhatsNewState = (
  userId: string,
  storage: StorageLike | null = getStorage(),
): WhatsNewStoredState => {
  const key = getWhatsNewStorageKey(userId);
  // A memory copy only exists while storage is refusing writes (see writeWhatsNewState), so
  // it is newer than anything stored — prefer it, or Hide/Dismiss would revert on re-read.
  const inMemory = memoryState.get(key);
  if (inMemory) return inMemory;
  try {
    const raw = storage?.getItem(key);
    if (raw) return normalize(JSON.parse(raw));
  } catch {
    /* unreadable or corrupt — treat as empty */
  }
  return emptyState();
};

export const writeWhatsNewState = (
  userId: string,
  state: WhatsNewStoredState,
  storage: StorageLike | null = getStorage(),
) => {
  const key = getWhatsNewStorageKey(userId);
  let persisted = false;
  try {
    if (storage) {
      storage.setItem(key, JSON.stringify(state));
      persisted = true;
    }
  } catch {
    /* quota / blocked storage — the in-memory copy below covers this session */
  }
  // Keep a memory copy only when storage failed, so a working localStorage stays the source
  // of truth (and other tabs' writes, picked up via the storage event, aren't shadowed).
  if (persisted) memoryState.delete(key);
  else memoryState.set(key, state);
  try {
    window.dispatchEvent(
      new CustomEvent(WHATS_NEW_CHANGED_EVENT, { detail: { userId } }),
    );
  } catch {
    /* no window (SSR/tests without DOM) */
  }
};

/** Capabilities of the signed-in account, used to decide which items apply to them. */
export type WhatsNewAudienceContext = {
  /** The tracking account itself (sees Settings → tracked fields). */
  canTrack: boolean;
  /** Can write the owner's observations (tracking owner or write-link spouse). */
  canWrite: boolean;
  optionalFields?: Record<string, boolean> | null;
  /** Shares the chart with at least one instructor; undefined while unknown. */
  hasInstructor?: boolean;
};

/** Structural subset of the owner context, so this module stays free of Supabase imports. */
type OwnerContextLike =
  | { status: "unauthenticated" | "needs-onboarding" }
  | {
      status: "ready";
      actorUserId: string;
      actorRole: string;
      canWriteObservations: boolean;
      viewingClientUserId: string | null;
      couple: { optional_fields: Record<string, boolean> | null };
    };

/**
 * Map the owner context to a what's-new audience, or null when the account gets no what's-new
 * at all: instructors (no tracking data; no Getting Started) and anyone mid client-view.
 */
export const getWhatsNewAudience = (
  ctx: OwnerContextLike | undefined | null,
): { userId: string; audience: WhatsNewAudienceContext } | null => {
  if (!ctx || ctx.status !== "ready") return null;
  if (ctx.actorRole === "instructor" || ctx.viewingClientUserId) return null;
  return {
    userId: ctx.actorUserId,
    audience: {
      canTrack: ctx.actorRole === "tracking",
      canWrite: ctx.canWriteObservations,
      optionalFields: ctx.couple.optional_fields,
    },
  };
};

export const isWhatsNewItemEligible = (
  item: WhatsNewItem,
  audience: WhatsNewAudienceContext,
) => {
  if (item.audience === "tracking" && !audience.canTrack) return false;
  if (item.audience === "write" && !audience.canWrite) return false;
  if (
    item.hideWhenFieldEnabled &&
    audience.optionalFields?.[item.hideWhenFieldEnabled] === true
  ) {
    return false;
  }
  if (item.instructorLink === "linked" && audience.hasInstructor !== true)
    return false;
  if (item.instructorLink === "none" && audience.hasInstructor !== false)
    return false;
  return true;
};

const stripHash = (path: string) => path.split("#")[0].split("?")[0];

export type WhatsNewItemView = {
  item: WhatsNewItem;
  done: boolean;
  /** Not done yet — carries the New badge in the dialog. */
  isNew: boolean;
};

export type UseWhatsNewOptions = {
  /** Null while the account is loading or when it gets no what's-new (see getWhatsNewAudience). */
  userId: string | null | undefined;
  audience: WhatsNewAudienceContext | null | undefined;
  release?: WhatsNewRelease;
};

export type UseWhatsNewResult = {
  release: WhatsNewRelease;
  /** Every item that applies to this account, in release order. Items never disappear. */
  items: WhatsNewItemView[];
  /**
   * The "Review the latest release" step exists for this account (eligible, not an
   * instructor). Permanent: it never expires or hides; it just becomes done.
   */
  releaseAvailable: boolean;
  /**
   * The release step is complete: every required (non-`optional`) card is done (its CTA,
   * visiting it, or its own "Mark as done"), or the step itself was marked done. Opening the
   * dialog alone does not complete it.
   */
  reviewed: boolean;
  /**
   * The latest release is still uncompleted — drives the Get Started nav dot, the step's New
   * badge and the home-card resurfacing.
   */
  hasNew: boolean;
  isItemDone: (id: WhatsNewItemId) => boolean;
  /** A nav destination has an uncompleted item behind it, while the release is uncompleted. */
  isPathNew: (pathname: string) => boolean;
  markDone: (id: WhatsNewItemId) => void;
  /** Mark done every item whose `visitPaths` include this pathname. */
  markPathVisited: (pathname: string) => void;
  /** Complete the release step via its own "Mark as done": marks every listed card done too. */
  markReviewed: () => void;
  /** The home-screen card was dismissed while this release was showing. */
  homeCardDismissed: boolean;
  dismissHomeCard: () => void;
};

export const useWhatsNew = ({
  userId,
  audience,
  release = WHATS_NEW_RELEASE,
}: UseWhatsNewOptions): UseWhatsNewResult => {
  const enabled = Boolean(userId && audience);
  const [state, setState] = useState<WhatsNewStoredState>(() =>
    userId ? readWhatsNewState(userId) : emptyState(),
  );

  // Re-read on user switch and whenever another hook instance (nav vs. page) or tab writes.
  useEffect(() => {
    if (!userId) {
      setState(emptyState());
      return;
    }
    const reload = () => setState(readWhatsNewState(userId));
    reload();
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string }>).detail;
      if (!detail?.userId || detail.userId === userId) reload();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === getWhatsNewStorageKey(userId)) reload();
    };
    window.addEventListener(WHATS_NEW_CHANGED_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(WHATS_NEW_CHANGED_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [userId]);

  // Read fresh before every write so concurrent instances never clobber each other.
  const write = useCallback(
    (mutate: (current: WhatsNewStoredState) => WhatsNewStoredState | null) => {
      if (!userId) return;
      const next = mutate(readWhatsNewState(userId));
      if (!next) return;
      setState(next);
      writeWhatsNewState(userId, next);
    },
    [userId],
  );

  const markItemsDone = useCallback(
    (ids: string[]) =>
      write((current) => {
        const pending = ids.filter((id) => !current.items[id]?.doneAt);
        if (pending.length === 0) return null;
        const stamp = new Date().toISOString();
        const items = { ...current.items };
        for (const id of pending) items[id] = { doneAt: stamp };
        return { ...current, items };
      }),
    [write],
  );

  const setReleaseField = useCallback(
    (field: keyof ReleaseRecord) =>
      write((current) => {
        if (current.releases[release.id]?.[field]) return null;
        return {
          ...current,
          releases: {
            ...current.releases,
            [release.id]: {
              ...current.releases[release.id],
              [field]: new Date().toISOString(),
            },
          },
        };
      }),
    [write, release.id],
  );

  const items = useMemo<WhatsNewItemView[]>(() => {
    if (!enabled || !audience) return [];
    return release.items
      .filter((item) => isWhatsNewItemEligible(item, audience))
      .map((item) => {
        const done = Boolean(state.items[item.id]?.doneAt);
        return { item, done, isNew: !done };
      });
  }, [enabled, audience, release, state]);

  // A required instructor-gated item may still appear once the instructor lookup resolves (the
  // nav never runs it), so "every card done" can't be decided until then — otherwise the nav
  // would call the step complete while Getting Started still lists an open card.
  const hasUnresolvedItems =
    enabled &&
    Boolean(audience) &&
    audience?.hasInstructor === undefined &&
    release.items.some(
      (item) =>
        item.instructorLink &&
        !item.optional &&
        isWhatsNewItemEligible(item, {
          ...audience,
          hasInstructor: item.instructorLink === "linked",
        }),
    );

  const releaseRecord = state.releases[release.id];
  const releaseAvailable = enabled && items.length > 0;
  const requiredItems = items.filter((view) => !view.item.optional);
  const allItemsDone =
    releaseAvailable && !hasUnresolvedItems && requiredItems.every((view) => view.done);
  const reviewed = Boolean(releaseRecord?.completedAt) || allItemsDone;
  const hasNew = releaseAvailable && !reviewed;

  // Record completion once every card is done, so instances that can't see every card (the nav)
  // agree with Getting Started.
  useEffect(() => {
    if (allItemsDone && !releaseRecord?.completedAt) setReleaseField("completedAt");
  }, [allItemsDone, releaseRecord?.completedAt, setReleaseField]);

  const isItemDone = useCallback(
    (id: WhatsNewItemId) =>
      items.some((view) => view.item.id === id && view.done),
    [items],
  );

  const isPathNew = useCallback(
    (pathname: string) => {
      if (!hasNew) return false;
      const path = stripHash(pathname);
      return items.some(
        (view) => !view.done && view.item.visitPaths?.includes(path),
      );
    },
    [items, hasNew],
  );

  const markDone = useCallback(
    (id: WhatsNewItemId) => markItemsDone([id]),
    [markItemsDone],
  );

  const markPathVisited = useCallback(
    (pathname: string) => {
      const path = stripHash(pathname);
      markItemsDone(
        release.items
          .filter((item) => item.visitPaths?.includes(path))
          .map((item) => item.id),
      );
    },
    [release, markItemsDone],
  );

  const markReviewed = useCallback(() => {
    markItemsDone(items.map((view) => view.item.id));
    setReleaseField("completedAt");
  }, [items, markItemsDone, setReleaseField]);
  const dismissHomeCard = useCallback(
    () => setReleaseField("homeCardDismissedAt"),
    [setReleaseField],
  );

  return {
    release,
    items,
    releaseAvailable,
    reviewed,
    hasNew,
    isItemDone,
    isPathNew,
    markDone,
    markPathVisited,
    markReviewed,
    homeCardDismissed: Boolean(releaseRecord?.homeCardDismissedAt),
    dismissHomeCard,
  };
};
